import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { GlobalDataContext } from "../../contexts/GlobalData";
import { PageTagContext } from "../../contexts/PageTag";
import { SnackbarContext } from "../../contexts/Snackbar";
import * as urlPaths from "../../enums/UrlPaths";
import { STACIONAR_EMAIL } from "../../features/home/HomePage";
import { snackbarSeverity } from "../../hooks/useSnackbar";
import useUser from "../../hooks/useUser";
import * as Functions from "../../utils/Functions";
import logger from "../../utils/logger";
import api from "../../utils/api/authApi";

// Component ensures the user can't access any paths unless logged in
// Any frontend action that checks a user permission to view a page should be done here
//
// roles - the roles allowed to open the page, an empty array means every logged in user can
// permissions - the permissions an admin or employee needs (all of them) to open the page
// Master can open every page, and so can the stacionar account (profile email STACIONAR_EMAIL)
export default function PrivateRoute({ children, tag = null, roles: allowedRoles = [], permissions = [] }) {
  const { currentClinic, roles, isLoading, booleans, profile, data, currentRole } = useUser();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const navigate = useNavigate();
  const { setCurrentTag } = React.useContext(PageTagContext);
  const queryClient = useQueryClient();
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const { t } = useTranslation();

  logger.debug("[PrivateRoute] Render — tag:", tag, "| isLoading:", isLoading, "| currentClinic:", currentClinic, "| roles:", roles);

  async function refreshTokens() {
    logger.debug("[PrivateRoute] refreshTokens — checking refresh token validity");
    if (Functions.isRefreshTokenValid()) {
      logger.debug("[PrivateRoute] refreshTokens — refresh token valid, refreshing...");
      let res = await Functions.refreshTokens();
      logger.debug("[PrivateRoute] refreshTokens — response:", res);
    } else {
      logger.warn("[PrivateRoute] refreshTokens — refresh token invalid, logging out");
      Functions.logout();
      navigate(urlPaths.LOGIN);
    }
  }

  async function isSessionValid() {
    logger.debug("[PrivateRoute] isSessionValid — checking session");
    if (!Functions.isRefreshTokenValid()) {
      logger.warn("[PrivateRoute] isSessionValid — refresh token invalid, logging out");
      Functions.logout();
      const lastUrl = window.location.pathname;
      if (!lastUrl.endsWith(urlPaths.LOGIN)) {
        logger.debug("[PrivateRoute] isSessionValid — saving last visited URL:", lastUrl);
        Functions.setLastVisitedUrl(lastUrl);
      }
      navigate(urlPaths.LOGIN);
      return false;
    } else {
      if (!Functions.isTokenValid()) {
        logger.debug("[PrivateRoute] isSessionValid — access token invalid, attempting refresh");
        let res = await refreshTokens();
      } else {
        logger.debug("[PrivateRoute] isSessionValid — access token valid, session OK");
      }
      return true;
    }
  }

  const minute = 1000 * 60;

  const isRestricted = allowedRoles.length > 0 || permissions.length > 0;

  function isAccessAllowed() {
    if (!isRestricted || roles.master || profile?.email === STACIONAR_EMAIL) {
      return true;
    }
    if (allowedRoles.length > 0 && !allowedRoles.includes(currentRole)) {
      return false;
    }
    if (roles.adminOrEmployee && !permissions.every((permission) => isPermissionAllowed(permission))) {
      return false;
    }
    return true;
  }

  // Computed during render so a page the user can't access is never mounted,
  // instead of every page redirecting from its own useEffect after it rendered
  const isAccessDenied = isRestricted && !isLoading && !isAccessAllowed();

  useEffect(() => {
    logger.debug("[PrivateRoute] useEffect [tag] — isLoading:", isLoading, "| tag:", tag);
    if (!isLoading) {
     if (booleans.needToChangePassword) {
        navigate(urlPaths.CHANGE_FORGOTTEN_PASSWORD);
        return;
      } else if (!roles.master && Functions.isEmptyString(profile?.phoneNumber)) {
        logger.warn("[PrivateRoute] useEffect [tag] — phone number missing, redirecting to phone setup");
        Functions.navigateToPhoneSetup(navigate);
      } else if (isAccessDenied) {
        logger.warn("[PrivateRoute] useEffect [tag] — role or permission not allowed, redirecting home — role:", currentRole, "| roles:", allowedRoles, "| permissions:", permissions);
        navigate(urlPaths.HOME);
        return;
      }
    }
    setCurrentTag(tag);
    // Only a tag change or the access result flipping (e.g. user data finished loading) should re-run this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag, isAccessDenied]);

  useEffect(() => {
    logger.debug("[PrivateRoute] useEffect [] — validating session on mount");
    isSessionValid();
    logger.debug("[PrivateRoute] useEffect [] — setting up token refresh interval (every 30 min)");
    const intervalId = setInterval(refreshTokens, minute * 30);
    return () => {
      logger.debug("[PrivateRoute] useEffect [] — clearing token refresh interval");
      clearInterval(intervalId);
    };
  }, []);

  // Synchronous on purpose: this used to call the async isSessionValid() on
  // every render, which always returned a (truthy) Promise and, whenever the
  // access token had expired, fired yet another concurrent token refresh per
  // render. The session is checked on mount and on the refresh interval; here
  // we only avoid rendering the page once the refresh token has run out, and
  // let isSessionValid() do the logout + redirect outside of render.
  const isRefreshTokenValid = Functions.isRefreshTokenValid();
  useEffect(() => {
    if (!isRefreshTokenValid) {
      logger.warn("[PrivateRoute] refresh token invalid on render, logging out and redirecting");
      isSessionValid();
    }
    // isSessionValid is recreated every render; only a validity flip should re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshTokenValid]);

  // Will log a user out only when the session genuinely can't be recovered.
  // A 401 first gets one token refresh + retry of the original request: the
  // request may have gone out headerless because its own refresh lost a race,
  // or carrying an access token that expired in flight.
  useEffect(() => {
    logger.debug("[PrivateRoute] useEffect [] — registering api response interceptor");
    const interceptorId = api.interceptors.response.use(
      (response) => {
        logger.debug("[PrivateRoute] api interceptor — response OK:", response.status, response.config?.url);
        return response;
      },
      async (error) => {
        const status = error?.response?.status;
        logger.error("[PrivateRoute] api interceptor — error status:", status, "| code:", error.code, "| url:", error.config?.url);
        if (status === 401 && error.config && !error.config._retriedAfterRefresh && Functions.isRefreshTokenValid()) {
          const res = await Functions.refreshTokens();
          if (res?.status === 200) {
            logger.warn("[PrivateRoute] api interceptor — 401, tokens refreshed, retrying:", error.config.url);
            error.config._retriedAfterRefresh = true;
            return api(error.config);
          }
          if (!Functions.isRefreshRejected(res)) {
            logger.error("[PrivateRoute] api interceptor — token refresh failed without rejecting the session, keeping tokens:", res?.status);
            return Promise.reject(error);
          }
        }
        if (status === 401) {
          logger.warn("[PrivateRoute] api interceptor — 401 Unauthorized, logging out");
          Functions.logout();
          navigate(urlPaths.LOGIN);
          return Promise.reject(error);
        }
        if (String(error.code) === "ERR_NETWORK") {
          logger.error("[PrivateRoute] api interceptor — network error, logging out:", error);
          handleSnackbar("NetworkError", snackbarSeverity.ERROR);
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }
    );
    return () => {
      logger.debug("[PrivateRoute] useEffect [] — ejecting api response interceptor");
      api.interceptors.response.eject(interceptorId);
    };
  }, []);

  if (!isRefreshTokenValid || (isRestricted && isLoading) || isAccessDenied) {
    return null;
  }
  return children;
}